import * as Popover from '@radix-ui/react-popover';
import { ChevronDown } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { type ControlSize, Input } from './index';

/**
 * Shared themed panel for table tools and cell choices, including keyboard dismissal.
 * Settings panels use the standard fixed width so their fields line up; `compact`
 * hugs its content for short action menus, which would otherwise leave a mostly
 * empty panel.
 */
export function ControlPopover({
  label,
  trigger,
  children,
  open,
  size = 'default',
  onOpenChange,
}: {
  label: string;
  trigger: ReactElement;
  children: ReactNode;
  open?: boolean;
  size?: ControlSize;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Popover.Root modal open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={
            size === 'compact'
              ? 'ui-control-popover ui-control-popover--compact'
              : 'ui-control-popover'
          }
          aria-label={label}
          align="end"
          sideOffset={6}
          collisionPadding={12}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function CellTags({ labels }: { labels: string[] }) {
  return (
    <span className="ui-cell-tags">
      {labels.map((label, index) => (
        <span
          className="ui-cell-tag"
          data-tone={
            /^(done|completed)$/i.test(label)
              ? 'success'
              : /^(in progress|working)$/i.test(label)
                ? 'focus'
                : 'neutral'
          }
          key={`${index}:${label}`}
        >
          {label}
        </span>
      ))}
    </span>
  );
}

export function MultiSelectCell({
  disabled,
  label,
  options,
  value,
  onChange,
}: {
  disabled?: boolean;
  label: string;
  options: { id: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <ControlPopover
      label={`Choose ${label}`}
      trigger={
        <button disabled={disabled} type="button" aria-label={label} className="ui-cell-choice">
          <CellTags labels={value.map(id => options.find(o => o.id === id)?.label ?? id)} />
          <ChevronDown aria-hidden="true" size={14} />
        </button>
      }
    >
      <strong>{label}</strong>
      <div className="ui-cell-options">
        {options.length ? (
          options.map(option => (
            <label key={option.id}>
              <Input
                type="checkbox"
                checked={value.includes(option.id)}
                onChange={e =>
                  onChange(
                    e.target.checked ? [...value, option.id] : value.filter(id => id !== option.id),
                  )
                }
              />
              {option.label}
            </label>
          ))
        ) : (
          <p>Add options in table column settings.</p>
        )}
      </div>
    </ControlPopover>
  );
}
